/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import { formatLocalizedDate } from '@/shared/utils/date';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FF_APP_FACEBOOK, FF_APP_INSTAGRAM_NATIVE, FF_APP_FACEBOOK_ADS } from '@/shared/utils/feature-flag';
import {
  collectImageRefs,
  deselectInstagramWithoutImages,
  resolvePublishHasImages,
  shouldShowInstagramPortal,
  isInstagramOptionLocked,
  type ImageRefInput,
} from '@/shared/utils/portal-publish';
import { Anchor, Button, DismissIconButton, Text } from '@/shared/ui';
import { isBelowMinimumAdBudget } from '@/shared/utils/ad-dialog-helpers';
import { getAllPortals } from './publish-dialog/constants';
import { usePublishDialog } from './publish-dialog/use-publish-dialog';
import { useOlxPhone } from './publish-dialog/olx-phone';
import { olxQuotaBlock, useOlxBalance } from './publish-dialog/olx-balance';
import { olxMissingFields, useOlxReadiness } from './publish-dialog/olx-readiness';
import { hasOlxAd, OlxPhonePanel, OlxSingleAdNote } from './publish-dialog/OlxPhonePanel';
import { OlxDealPanel } from './publish-dialog/OlxDealPanel';
import { mustChooseOlxDeal, olxDealMissing } from './publish-dialog/olx-deal';
import { useAdDialogs } from './ad-dialogs/use-ad-dialogs';
import { PortalRow, FacebookLeadAdPanel, InstagramLeadAdPanel, SchedulePublicationPanel } from './publish-dialog/parts';
import { usePropertyImages } from './PropertyDetailView/hooks/usePropertyImages';
import { adSettingsHref } from './ad-dialogs/AdSetupNotice';

/**
 * Empty state when no portals are connected — CTA into agency Facebook settings.
 */
function NoPortalsEnabledState({ agencyId, t }: { agencyId: string; t: (key: string) => string }) {
  return (
    <div className="flex flex-col items-center gap-(--space-3) py-(--space-4) px-(--space-3) rounded-(--radius-lg) text-center bg-[var(--surface-secondary)] border border-dashed border-[var(--border-subtle)]">
      <Text className="m-0 text-xs leading-relaxed text-[var(--text-secondary)]">
        {t('publish.noPortalsEnabled')}
      </Text>
      <Anchor
        href={`/app/agencies/${agencyId}/settings?section=facebook`}
        underline="never"
        className="text-xs font-semibold no-underline px-(--space-3) py-(--space-1-5) rounded-(--radius-md) transition-colors bg-[var(--seed-base)] text-[var(--surface-primary)]"
        data-testid="no-portals-settings-link"
      >
        {t('publish.noPortalsEnabledLink')}
      </Anchor>
    </div>
  );
}

/**
 * Modal dialog that lets a broker publish a property to the enabled portals.
 * Renders selectable portal rows, optional schedule + Facebook/Instagram
 * lead-ad panels, and a publish action inside a body-level portal overlay.
 */
export function PublishDialog({ propertyId, agencyId, onClose, onPublished, propertyImages }: {
  propertyId: string;
  agencyId: string;
  onClose: () => void;
  onPublished?: () => void;
  propertyImages?: ImageRefInput[];
}) {
  const { t } = useTranslation('features/properties');
  const { confirmSpend, showAdError, dialogs: adDialogs } = useAdDialogs();
  const [fbCreateLeadAd, setFbCreateLeadAd] = useState(false);
  const [igCreateLeadAd, setIgCreateLeadAd] = useState(false);
  const fallbackRefs = collectImageRefs(propertyImages);
  const { imageUrls, loading: imagesLoading } = usePropertyImages(agencyId, propertyId, fallbackRefs);
  // Optimistic while the gallery fetch runs so empty media.images does not flash the
  // Instagram "needs images" lock before S3 view URLs resolve.
  const hasImages = resolvePublishHasImages(fallbackRefs, imageUrls) || imagesLoading;
  const [olxSelected, setOlxSelected] = useState(false);
  const olxPhone = useOlxPhone(agencyId, propertyId, olxSelected);

  const {
    selected, setSelected, publishing, results, savedStatus, loading,
    enabledPortals, fbHasPaymentMethod, igAccountId, handlePublish,
    scheduleEnabled, setScheduleEnabled, scheduledAt, setScheduledAt,
    fbLeadAdBudget, setFbLeadAdBudget, igLeadAdBudget, setIgLeadAdBudget, adReadiness, olxBlockReason, olxRenewsAt,
  } = usePublishDialog({
    propertyId, agencyId, onPublished, onClose, t, fbCreateLeadAd,
    igCreateLeadAd, hasImages, confirmSpend, showAdError, olxPhone: olxPhone.phone,
    olxTransaction: olxPhone.transaction,
  });

  // OLX refuses ads without its required fields (photos, price, CEP,
  // category, bedrooms): OLX stays locked, naming what to fill in.
  const olxMissing = olxMissingFields(useOlxReadiness(agencyId, propertyId, enabledPortals.includes('olx')), hasImages);
  // Before anything goes to OLX, its plan balance is read live: a new ad
  // without a slot left is locked instead of failing after the send.
  const olxBalance = useOlxBalance(agencyId, enabledPortals.includes('olx'));
  const olxBlock = olxQuotaBlock(olxBlockReason, olxBalance, hasOlxAd(savedStatus));
  const olxLock = olxLockReason(olxBlock, olxMissing);

  useEffect(() => {
    setSelected((prev) => {
      let next = deselectInstagramWithoutImages(prev, hasImages);
      if (!igAccountId) next = next.filter((id) => id !== 'instagram');
      // A locked OLX (missing fields, no slot left, …) is not left selected.
      if (olxLock) next = next.filter((id) => id !== 'olx');
      return next;
    });
  }, [hasImages, igAccountId, setSelected, olxLock]);

  // OLX stays listed but locked, saying why (no plan, unconfirmed account,
  // reconnect) -- the agency sees what is missing instead of losing the option.
  const allPortals = lockOlxOption(
    getAllPortals(t), olxLock, t, olxBalance?.renewsAt ?? olxRenewsAt, olxMissing,
  );
  const visiblePortals = allPortals.filter((p) => {
    if (p.id === 'instagram') {
      return shouldShowInstagramPortal(FF_APP_FACEBOOK || FF_APP_INSTAGRAM_NATIVE, enabledPortals);
    }
    const backendId = p.backendId ?? p.id;
    return enabledPortals.includes(backendId) || enabledPortals.includes(p.id);
  });

  // OLX refuses an ad without a phone: the broker picks it while OLX is selected.
  const olxChosen = selected.includes('olx') && !allPortals.find((p) => p.id === 'olx')?.unavailable;
  useEffect(() => { setOlxSelected(olxChosen); }, [olxChosen]);
  // A property offered for sale AND rent gets one OLX ad: the broker picks its main deal first.
  const olxPhoneMissing = olxChosen && (!olxPhone.phone || olxDealMissing(olxPhone.deals, olxPhone.dealChoice));

  const fbSelected = selected.includes('facebook_feed') || selected.includes('facebook_marketplace');
  const igSelected = selected.includes('instagram');
  // Meta rejects an ad set below its minimum daily budget, so the dialog does
  // not let a Lead Ad go out under it (the panel above says why).
  const leadAdBudgetTooLow =
    (fbSelected && FF_APP_FACEBOOK && FF_APP_FACEBOOK_ADS && fbCreateLeadAd && isBelowMinimumAdBudget(fbLeadAdBudget))
    || (igSelected && FF_APP_FACEBOOK_ADS && igCreateLeadAd && isBelowMinimumAdBudget(igLeadAdBudget));
  const allNotConnected = results && Object.values(results).every(
    (r) => (r as { status?: string; reason?: string }).status === 'error'
      && (r as { reason?: string }).reason === 'not_connected',
  );
  const noPortalsEnabled = !loading && visiblePortals.length === 0;

  const publishableCount = selected.filter((id) => {
    if (id === 'instagram' && isInstagramOptionLocked(hasImages, igAccountId)) return false;
    const portal = allPortals.find((p) => p.id === id);
    return portal && !portal.unavailable;
  }).length;

  const publishLabel = resolvePublishLabel({
    t, publishing, scheduleEnabled, publishableCount,
  });

  return createPortal(
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--overlay-scrim)]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-sm mx-(--space-4) p-(--space-4) rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] shadow-[var(--shadow-lg)] space-y-(--space-3)">
        <div className="flex items-center justify-between">
          <Text as="span" className="text-sm font-semibold text-[var(--text-primary)]">{t('publish.title')}</Text>
          <DismissIconButton label={t('common:a11y.close')} onClick={onClose} />
        </div>

        {loading ? (
          <Text className="m-0 text-xs text-[var(--text-tertiary)]">{t('publish.loading')}</Text>
        ) : noPortalsEnabled ? (
          <NoPortalsEnabledState agencyId={agencyId} t={t} />
        ) : (
          <>
            <div className="space-y-(--space-2)">
              {visiblePortals.map((p) => (
                <PortalRow
                  key={p.id}
                  p={p}
                  t={t}
                  selected={selected}
                  setSelected={setSelected}
                  results={results}
                  savedStatus={savedStatus}
                  hasImages={hasImages}
                  igAccountId={igAccountId}
                />
              ))}
            </div>

            {olxChosen && <OlxSingleAdNote t={t} existing={hasOlxAd(savedStatus)} />}
            {olxChosen && mustChooseOlxDeal(olxPhone.deals) && (
              <OlxDealPanel t={t} deals={olxPhone.deals} choice={olxPhone.dealChoice} setChoice={olxPhone.setDealChoice} />
            )}
            {olxChosen && <OlxPhonePanel t={t} {...olxPhone} />}

            {fbSelected && FF_APP_FACEBOOK && FF_APP_FACEBOOK_ADS && (
              <FacebookLeadAdPanel
                t={t}
                fbHasPaymentMethod={fbHasPaymentMethod}
                fbCreateLeadAd={fbCreateLeadAd}
                setFbCreateLeadAd={setFbCreateLeadAd}
                fbLeadAdBudget={fbLeadAdBudget}
                setFbLeadAdBudget={setFbLeadAdBudget}
                missing={adReadiness.facebook.leadAd}
                settingsHref={adSettingsHref(agencyId)}
              />
            )}

            {igSelected && FF_APP_FACEBOOK_ADS && (
              <InstagramLeadAdPanel
                t={t}
                igHasPaymentMethod={fbHasPaymentMethod}
                igCreateLeadAd={igCreateLeadAd}
                setIgCreateLeadAd={setIgCreateLeadAd}
                igLeadAdBudget={igLeadAdBudget}
                setIgLeadAdBudget={setIgLeadAdBudget}
                missing={adReadiness.instagram.leadAd}
                settingsHref={adSettingsHref(agencyId)}
              />
            )}

            {fbCreateLeadAd && igCreateLeadAd && (
              <Text className="m-0 text-xs text-[var(--text-secondary)]" data-testid="combined-lead-ad-budget">
                {t('publish.combinedLeadAdBudget', { total: fbLeadAdBudget + igLeadAdBudget })}
              </Text>
            )}

            <SchedulePublicationPanel
              t={t}
              scheduleEnabled={scheduleEnabled}
              setScheduleEnabled={setScheduleEnabled}
              scheduledAt={scheduledAt}
              setScheduledAt={setScheduledAt}
            />

            <Button
              type="button"
              onClick={handlePublish}
              disabled={publishing || !publishableCount || (scheduleEnabled && !scheduledAt) || leadAdBudgetTooLow || olxPhoneMissing}
              className="w-full py-(--space-2) text-xs font-bold rounded-(--radius-md) cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed bg-[var(--seed-base)] text-[var(--surface-primary)]"
              data-testid="publish-submit-btn"
            >
              {publishLabel}
            </Button>

            {allNotConnected && (
              <Text className="m-0 text-xs text-[var(--status-warning-text)]">{t('publish.connectPortals')}</Text>
            )}
          </>
        )}
      </div>
    </div>
    {adDialogs}
    </>,
    document.body,
  );
}

/**
 * Resolves the primary CTA label for publish vs schedule.
 */
function resolvePublishLabel(args: {
  t: (key: string, opts?: Record<string, unknown>) => string;
  publishing: boolean;
  scheduleEnabled: boolean;
  publishableCount: number;
}): string {
  if (args.publishing) return args.t('publish.publishing');
  if (args.scheduleEnabled) {
    return args.publishableCount !== 1
      ? args.t('publish.scheduleInPlural', { count: args.publishableCount })
      : args.t('publish.scheduleIn', { count: args.publishableCount });
  }
  return args.publishableCount !== 1
    ? args.t('publish.publishInPlural', { count: args.publishableCount })
    : args.t('publish.publishIn', { count: args.publishableCount });
}

/**
 * Why the OLX option is locked: the account's reason from the backend first
 * (reconnect, confirmation, terms, plan, quota), then a property missing a
 * field OLX requires (photos, price, CEP, category, bedrooms).
 *
 * @param blockReason - ``olx_block_reason`` from the settings, or null.
 * @param missingFields - Required OLX fields the property lacks.
 * @returns The reason key, or null when OLX can be selected.
 */
export function olxLockReason(blockReason: string | null, missingFields: string[]): string | null {
  if (blockReason) return blockReason;
  return missingFields.length > 0 ? 'olx_missing_fields' : null;
}

/** The sentence shown under the locked OLX option. */
function olxLockDescription(
  reason: string, t: (key: string, options?: Record<string, unknown>) => string,
  renewsAt: string | null | undefined, missingFields: string[],
): string {
  if (reason === 'olx_missing_fields') {
    const fields = missingFields.map((field) => t(`publish.olxFields.${field}`)).join(', ');
    return t('publish.reason.olx_missing_fields', { fields });
  }
  const date = reason === 'olx_no_slots' ? formatLocalizedDate(renewsAt) : '';
  return date ? t('publish.reason.olx_no_slots_until', { date }) : t(`publish.reason.${reason}`);
}

/**
 * Marks the OLX option unavailable with the reason the backend gave.
 *
 * @param portals - The dialog's portal options.
 * @param reason - ``olx_block_reason`` from the settings, or null.
 * @param t - Translation function (properties namespace).
 * @returns The options, OLX locked when there is a reason.
 */
export function lockOlxOption<P extends { id: string; desc: string; unavailable?: boolean }>(
  portals: P[], reason: string | null, t: (key: string, options?: Record<string, unknown>) => string,
  renewsAt?: string | null, missingFields: string[] = [],
): P[] {
  if (!reason) return portals;
  const desc = olxLockDescription(reason, t, renewsAt, missingFields);
  return portals.map((p) => (p.id === 'olx' ? { ...p, unavailable: true, desc } : p));
}
